import { useState } from "react";
import { useDocumentStore } from "../store/documentStore";
import { useSessionStore } from "../store/sessionStore";
import { useAuthStore } from "../store/authStore";
import { ATTACHED_DOCUMENTS_TOTAL_MAX_BYTES } from "../lib/session";
import type { Session } from "../lib/session";

// AttachDocsModal — picker for the chat toolbar's "Docs" button.
// Lists library docs as checkboxes (checked = currently attached
// to this channel) and a list of channel-only docs (attached but
// not in this browser's library, e.g. from a teammate). Toggling
// checks attach/detach in place. Closes via backdrop click or X.

interface Props {
  session: Session;
  onClose: () => void;
}

export function AttachDocsModal({ session, onClose }: Props) {
  const libraryDocs = useDocumentStore((s) => s.documents);
  const attachDocument = useSessionStore((s) => s.attachDocument);
  const detachDocument = useSessionStore((s) => s.detachDocument);
  const userName = useAuthStore((s) => s.user?.name ?? "anon");
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const filterLower = filter.trim().toLowerCase();
  // Match by name substring OR tag substring. Empty filter = all.
  const filteredLibrary = filterLower.length === 0
    ? libraryDocs
    : libraryDocs.filter((d) =>
        d.name.toLowerCase().includes(filterLower) ||
        (d.tags ?? []).some((t) => t.includes(filterLower)),
      );

  const attached = session.attached_documents ?? [];
  const attachedTotal = attached.reduce((sum, d) => sum + d.bytes, 0);
  const usedPct = Math.round((attachedTotal / ATTACHED_DOCUMENTS_TOTAL_MAX_BYTES) * 100);

  // Match library docs to attached by content (sha-free: just byte
  // length + first 200 chars). Heuristic, good enough for "is this
  // library doc the same one already attached?" — the alt would be
  // hashing and storing a sha alongside, more state for marginal gain.
  function attachedMatch(libContent: string, libBytes: number) {
    return attached.find((a) =>
      a.bytes === libBytes && a.content.slice(0, 200) === libContent.slice(0, 200),
    );
  }

  // Attached docs that aren't in this browser's library — kept around
  // so users see the full channel context here (e.g. docs a teammate
  // attached). Detachable but not toggleable as a checkbox.
  const orphanAttached = attached.filter((a) =>
    !libraryDocs.some((l) => l.bytes === a.bytes && l.content.slice(0, 200) === a.content.slice(0, 200)),
  );

  function toggleLibraryDoc(libDocId: string) {
    setError(null);
    const lib = libraryDocs.find((d) => d.id === libDocId);
    if (!lib) return;
    const match = attachedMatch(lib.content, lib.bytes);
    if (match) {
      detachDocument(session.id, match.id);
    } else {
      try {
        attachDocument(session.id, lib.name, lib.content, userName);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  }

  /** Bulk actions on the library list. "all" attaches every
   *  unattached lib doc (stopping on first cap error and surfacing it);
   *  "none" detaches every lib doc currently attached; "invert"
   *  flips each lib doc's state. Orphan attached docs (not in this
   *  browser's library) aren't touched — explicit detach lives on
   *  their per-row buttons. */
  function bulk(action: "all" | "none" | "invert") {
    setError(null);
    for (const lib of libraryDocs) {
      const match = attachedMatch(lib.content, lib.bytes);
      const isAttached = !!match;
      let shouldBeAttached: boolean;
      if (action === "all") shouldBeAttached = true;
      else if (action === "none") shouldBeAttached = false;
      else shouldBeAttached = !isAttached;
      if (shouldBeAttached === isAttached) continue;
      if (shouldBeAttached) {
        try {
          attachDocument(session.id, lib.name, lib.content, userName);
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
          return; // stop on first cap error
        }
      } else if (match) {
        detachDocument(session.id, match.id);
      }
    }
  }

  /** Attach every (unattached) library doc carrying the given tag.
   *  Lets users curate bundles in the library ("rfc-set", "onboarding")
   *  and snap a whole bundle onto a channel in one click. */
  function attachAllWithTag(tag: string) {
    setError(null);
    for (const lib of libraryDocs) {
      if (!(lib.tags ?? []).includes(tag)) continue;
      if (attachedMatch(lib.content, lib.bytes)) continue;
      try {
        attachDocument(session.id, lib.name, lib.content, userName);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return;
      }
    }
  }

  // Unique tags across the library, alphabetized for stable display.
  const allTags = Array.from(
    new Set(libraryDocs.flatMap((d) => d.tags ?? [])),
  ).sort();

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Attach documents</h2>
        <div className="subtitle">
          Checked docs ride along with every AI turn as context. Content copies
          into the channel — your teammates see the same docs the AI sees.
        </div>

        {error && <div className="banner banner-error" style={{ margin: "8px 0" }}>{error}</div>}

        <div className="lib-quota" style={{ padding: "8px 0", borderBottom: "none" }}>
          <div className="lib-quota-bar"><div className="lib-quota-fill" style={{ width: `${Math.min(usedPct, 100)}%` }} /></div>
          <span>
            {(attachedTotal / 1024).toFixed(1)}KB of {ATTACHED_DOCUMENTS_TOTAL_MAX_BYTES / 1024}KB used on this channel
          </span>
        </div>

        {libraryDocs.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--fg-dim)", padding: "12px 0", textAlign: "center" }}>
            Your library is empty. Switch to <strong>Library</strong> in the title bar to add a .txt or .md file.
          </div>
        ) : (
          <>
            {libraryDocs.length > 4 && (
              <input
                type="text"
                className="attach-filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="filter by name or tag…"
                autoFocus
              />
            )}
            {libraryDocs.length > 1 && (
              <div className="attach-bulk">
                <span className="attach-bulk-label">bulk:</span>
                <button className="link-btn" onClick={() => bulk("all")}>all</button>
                <button className="link-btn" onClick={() => bulk("none")}>none</button>
                <button className="link-btn" onClick={() => bulk("invert")}>invert</button>
                {filterLower && (
                  <span className="attach-filter-hint">
                    showing {filteredLibrary.length} of {libraryDocs.length}
                  </span>
                )}
              </div>
            )}
            {allTags.length > 0 && (
              <div className="attach-tags">
                <span className="attach-bulk-label">attach all tagged:</span>
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    className="lib-tag attach-tag-btn"
                    onClick={() => attachAllWithTag(tag)}
                    title={`Attach every library doc tagged "${tag}"`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 300, overflowY: "auto" }}>
            {filteredLibrary.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--fg-dimmer)", padding: "12px 0", textAlign: "center" }}>
                No library docs match "{filter}".
              </div>
            )}
            {filteredLibrary.map((d) => {
              const match = attachedMatch(d.content, d.bytes);
              const checked = !!match;
              return (
                <label
                  key={d.id}
                  className="attach-row"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleLibraryDoc(d.id)}
                  />
                  <span className="attach-row-name">{d.name}</span>
                  <span className="attach-row-bytes">
                    {(d.bytes / 1024).toFixed(1)}KB
                  </span>
                  <div className="attach-row-preview">
                    <pre>{firstLines(d.content, 8)}</pre>
                  </div>
                </label>
              );
            })}
            </div>
          </>
        )}

        {orphanAttached.length > 0 && (
          <>
            <div style={{ fontSize: 11, color: "var(--fg-dimmer)", marginTop: 12, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Attached but not in your library
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {orphanAttached.map((a) => (
                <div
                  key={a.id}
                  className="checkbox-row"
                  style={{ padding: "6px 4px", display: "flex", alignItems: "center", gap: 8 }}
                >
                  <span style={{ flex: 1, fontSize: 13 }}>{a.name}</span>
                  <span style={{ fontSize: 10, color: "var(--fg-dimmer)", fontFamily: "ui-monospace, monospace" }}>
                    {(a.bytes / 1024).toFixed(1)}KB · by {a.attached_by}
                  </span>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: 11 }}
                    onClick={() => detachDocument(session.id, a.id)}
                  >
                    Detach
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="actions">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function firstLines(content: string, n: number): string {
  const lines = content.split("\n").slice(0, n);
  const joined = lines.join("\n");
  // Cap raw character count too so a doc with no newlines doesn't
  // blow out the preview popover.
  return joined.length > 600 ? joined.slice(0, 597) + "…" : joined;
}
